import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { useUserIdStore } from 'store/userIdStore';

// createUsersIdentityVerification 함수 타입
type CreateUsersIdentityVerification = (userId: string) => void;

/**
 * URL 리디렉션을 기반으로 사용자 인증을 처리하는 React Hook입니다.
 * - 현재 정책 상 포트원의 본인 인증은 PC는 팝업, 모바일은 Redirect URL로 처리를 한다.
 * - 모바일의 Redirect URL 안에 identityVerificationId를 searchParams로 넘겨주게 되는데, 이를 기반으로 identify api를 보내도록 한다.
 *
 * 이 훅은 URL의 `identityVerificationId`와 `code` 쿼리 매개변수를 확인하고,
 * 특정 조건이 충족되면 `createUsersIdentityVerification` 함수를 호출합니다.
 *
 * @param {CreateUsersIdentityVerification} createUsersIdentityVerification - 사용자 ID를 기반으로 인증을 처리하는 함수입니다.
 *
 * @example
 * ```tsx
 * const handleIdentityVerification = (userId: string) => {
 *   사용자 인증 처리 로직
 * };
 *
 * useCreateIdentityVerificationWithRedirect(handleIdentityVerification);
 * ```
 */
export function useCreateIdentityVerificationWithRedirect(
  createUsersIdentityVerification: CreateUsersIdentityVerification
) {
  const userId = useUserIdStore((state) => state.userId);

  const [searchParams, setSearchParams] = useSearchParams();
  const identityVerificationId = searchParams.get('identityVerificationId');
  const code = searchParams.get('code');

  useEffect(() => {
    if (!userId) return;

    if (identityVerificationId) {
      createUsersIdentityVerification(identityVerificationId);
      setSearchParams(new URLSearchParams());
    }
  }, [userId]);
}
